import { resolvePipelineStage, resolveStageLabel } from '../../utils/workspaceCaseStage'

/** Shared pipeline stage + transition rules (Investigation Pipeline + Investigation Case). */

export const STAGE_TO_STATUS = {
  investigation: 'INVESTIGATING',
  pending_evidence: 'PENDING_EVIDENCE',
  suspicion_formed: 'SUSPICION_FORMED',
  smr_draft: 'SMR_DRAFT',
  under_review: 'PENDING_APPROVAL',
  filed: 'FILED',
  closed: 'CLOSED',
}

export const STAGES = [
  { id: 'investigation', label: 'Investigation' },
  { id: 'pending_evidence', label: 'Pending Evidence' },
  { id: 'suspicion_formed', label: 'Suspicion Formed' },
  { id: 'smr_draft', label: 'SMR Draft' },
  { id: 'under_review', label: 'SMR Approved' },
  { id: 'filed', label: 'SMR Filed' },
  { id: 'closed', label: 'Closed' },
]

export const TRANSITION_RULES = {
  investigation: {
    forward: [
      { to: 'pending_evidence', label: 'Pause — Pending Evidence', allowedRoles: ['co', 'manager'], conditions: [{ id: 'evidence_identified', label: 'I have identified what evidence is outstanding' }, { id: 'request_sent', label: 'I have sent the request or notified the relevant party' }, { id: 'deadline_set', label: 'I have set a follow-up deadline' }], requiresReason: true, reasonLabel: 'What are you waiting for?', warning: false },
      { to: 'suspicion_formed', label: 'Form Suspicion — Start SMR Clock', allowedRoles: ['co', 'manager'], conditions: [{ id: 'evidence_assessed', label: 'I have reviewed all available evidence' }, { id: 'innocent_considered', label: 'I have considered and ruled out innocent explanations' }, { id: 'grounds_documented', label: 'I have documented the grounds for suspicion in the investigation notes' }], requiresReason: true, reasonLabel: 'Basis for suspicion', warning: true, warningText: 'Legal event. SMR clock starts (3 business days). This record is permanent and cannot be deleted.' },
      { to: 'closed', label: 'Close — No Further Action', allowedRoles: ['co'], needsManagerApproval: true, conditions: [{ id: 'investigation_complete', label: 'I have completed and documented the investigation' }, { id: 'innocent_explanation', label: 'I have documented the innocent explanation' }], requiresReason: true, reasonLabel: 'Document the innocent explanation', warning: false },
    ],
    backward: [],
  },
  pending_evidence: {
    forward: [
      { to: 'investigation', label: 'Evidence Received — Resume', allowedRoles: ['co', 'manager'], conditions: [{ id: 'evidence_received', label: 'I confirm the requested evidence has been received' }], requiresReason: true, reasonLabel: 'What evidence was received?', warning: false },
      { to: 'suspicion_formed', label: 'Non-Response — Form Suspicion', allowedRoles: ['co', 'manager'], conditions: [{ id: 'timeout_elapsed', label: 'I confirm the deadline has passed without response' }, { id: 'grounds_documented', label: 'I have documented the grounds for suspicion in the investigation notes' }], requiresReason: true, reasonLabel: 'Basis for suspicion', warning: true, warningText: 'Legal event. SMR clock starts (3 business days).', direction: 'forward' },
    ],
    backward: [{ to: 'investigation', label: 'Return to Investigation', allowedRoles: ['manager'], conditions: [], requiresReason: true, reasonLabel: 'Reason for return', warning: true, warningText: 'Manager override.', direction: 'backward' }],
  },
  suspicion_formed: {
    forward: [{ to: 'smr_draft', label: 'Open SMR Draft', allowedRoles: ['co', 'manager'], conditions: [{ id: 'suspicion_documented', label: 'Suspicion grounds are recorded in the investigation record' }], requiresReason: false, direction: 'forward' }],
    backward: [{ to: 'investigation', label: 'Return to Investigation (Override)', allowedRoles: ['manager'], conditions: [{ id: 'legal_risk_noted', label: 'I acknowledge this is a high-risk override with legal implications' }], requiresReason: true, reasonLabel: 'Why reversing suspicion?', warning: true, warningText: 'CRITICAL. Reversing suspicion has legal implications. The SMR clock does not reset.', direction: 'backward' }],
  },
  smr_draft: {
    forward: [{ to: 'under_review', label: 'Submit for Manager Review', allowedRoles: ['co'], conditions: [{ id: 'narrative_complete', label: 'I have completed the SMR narrative' }, { id: 'evidence_referenced', label: 'I have referenced the supporting evidence in the narrative' }], requiresReason: true, reasonLabel: 'Note to manager', warning: false, direction: 'forward' }],
    backward: [{ to: 'suspicion_formed', label: 'Return to Suspicion Formed', allowedRoles: ['manager'], conditions: [], requiresReason: true, reasonLabel: 'Reason for return', warning: true, warningText: 'SMR clock still running.', direction: 'backward' }],
  },
  under_review: {
    forward: [{ to: 'filed', label: 'Approve and Submit to AUSTRAC', allowedRoles: ['manager'], conditions: [{ id: 'narrative_reviewed', label: 'I have reviewed the full SMR narrative' }, { id: 'evidence_confirmed', label: 'I have confirmed the evidence supports the filing' }], requiresReason: true, reasonLabel: 'Manager approval note', warning: false, direction: 'forward' }],
    backward: [{ to: 'smr_draft', label: 'Request Revision from CO', allowedRoles: ['manager'], conditions: [], requiresReason: true, reasonLabel: 'What needs revision?', warning: true, warningText: 'Revision consumes SMR clock time.', direction: 'backward' }],
  },
  filed: { forward: [], backward: [] },
  closed: { forward: [], backward: [] },
}

export function ruleChangesApiStatus(rule, currentApiStatus) {
  const target = STAGE_TO_STATUS[rule.to]
  if (!target) return true
  return String(target).toUpperCase() !== String(currentApiStatus || '').toUpperCase()
}

export function resolveCurrentUserMember(teamMembers, authUser) {
  if (!authUser) return null
  const uname = String(authUser.username || authUser.sub || '').trim().toLowerCase()
  if (!uname) return null
  return teamMembers.find((m) => String(m.username || '').trim().toLowerCase() === uname) || null
}

export function hasPipelineViewPermission(can) {
  return can('compliance-safe/investigations/pipeline', 'view')
    || can('compliance-safe/investigations', 'view')
    || can('compliance-safe/cases', 'view')
    || can('cases', 'view')
}

export function hasPipelineManagePermission(can) {
  return can('compliance-safe/investigations/pipeline', 'manage')
    || can('compliance-safe/investigations', 'manage')
    || can('compliance-safe/cases', 'manage')
    || can('cases', 'manage')
}

export function canUserTransitionCase(caseData, teamMembers, authUser, hasEditPermission, hasManagePipeline) {
  if (!hasEditPermission) return false
  if (hasManagePipeline) return true
  const assignedUserId = caseData?.assignee?.userId
  if (assignedUserId == null) return true
  const me = resolveCurrentUserMember(teamMembers, authUser)
  return me?.userId != null && Number(me.userId) === Number(assignedUserId)
}

/** Days in current pipeline stage before UI highlights as stale. */
export const STAGE_STALE_DAYS = 7

/** Typical venue CCTV retention from incident date (footage may be overwritten after this). */
export const CCTV_RETENTION_DAYS = 21

const MS_PER_DAY = 86400000

function parseDate(raw) {
  if (raw == null || raw === '') return null
  const d = raw instanceof Date ? raw : new Date(raw)
  return Number.isNaN(d.getTime()) ? null : d
}

/**
 * Timestamp for "how long in this column" — avoids using generic updated_at for SMR filed/closed.
 */
export function pickStageEnteredAt(apiCase, stage, status) {
  if (!apiCase) return null
  const st = (status || apiCase.status || 'INVESTIGATING').toUpperCase()
  const stageId = stage || resolvePipelineStage(apiCase)
  if (stageId === 'filed' || st === 'FILED') {
    return apiCase.resolved_at || apiCase.regulatory_report_date || apiCase.updated_at || apiCase.created_at
  }
  if (stageId === 'under_review' && st === 'PENDING_APPROVAL') {
    return apiCase.updated_at || apiCase.created_at
  }
  if (stageId === 'closed' && (st === 'RESOLVED' || st === 'CLOSED')) {
    return apiCase.closed_at || apiCase.resolved_at || apiCase.updated_at || apiCase.created_at
  }
  return apiCase.updated_at || apiCase.created_at
}

export function daysSince(date) {
  const d = parseDate(date)
  if (!d) return null
  return Math.floor((Date.now() - d.getTime()) / MS_PER_DAY)
}

/** True when case has been in the current (non-terminal) stage longer than STAGE_STALE_DAYS. */
export function getStageStaleInfo(stageEnteredAt, stageId) {
  if (['filed', 'closed'].includes(String(stageId || ''))) return null
  const daysInStage = daysSince(stageEnteredAt)
  if (daysInStage == null || daysInStage <= STAGE_STALE_DAYS) return null
  return {
    daysInStage,
    daysOverThreshold: daysInStage - STAGE_STALE_DAYS,
  }
}

/**
 * Human label for time in the current pipeline stage (not overall case age).
 * @param {'detail'|'pipeline'} context — detail pages name the stage; pipeline board columns already imply it.
 */
export function formatDaysInStageLabel(daysInStage, stageLabel, { context = 'pipeline' } = {}) {
  if (daysInStage == null) return ''
  if (context === 'detail' && stageLabel) return `${daysInStage}d in ${stageLabel}`
  return `${daysInStage}d in stage`
}

export function stageAgingTooltip(stageLabel, stageEnteredAt) {
  const days = daysSince(stageEnteredAt)
  const dayPart = days != null ? `${days} day(s)` : 'Time'
  const stagePart = stageLabel ? ` in “${stageLabel}”` : ' in this pipeline stage'
  return `${dayPart}${stagePart} — not overall case age (see “Opened … ago” in the header).`
}

/** Earliest incident / event time for CCTV retention (snapshot anchor, else case opened). */
export function pickIncidentDate(caseRow, snapshot) {
  const snap = snapshot && typeof snapshot === 'object' ? snapshot : (caseRow?.source_data_snapshot || {})
  const anchor = parseDate(
    snap.SnapShotTime || snap.snapshot_time || snap.TransactionTime || snap.transaction_time
      || snap.EventTime || snap.event_time || snap.StartTime || snap.start_time || snap.timestamp
  )
  if (anchor) return anchor
  return parseDate(caseRow?.created_at)
}

export function getCctvRetentionStatus(incidentDate, retentionDays = CCTV_RETENTION_DAYS) {
  const incident = parseDate(incidentDate)
  if (!incident) return null
  const expiryMs = incident.getTime() + retentionDays * MS_PER_DAY
  const daysLeft = Math.ceil((expiryMs - Date.now()) / MS_PER_DAY)
  return {
    incidentDate: incident,
    retentionDays,
    daysLeft,
    overdue: daysLeft <= 0,
    urgent: daysLeft > 0 && daysLeft <= 7,
  }
}

export function buildWorkflowCaseFromApi(apiCase, teamMembers = []) {
  const stage = resolvePipelineStage(apiCase)
  let assigneeMember = null
  if (apiCase.assigned_staff_id != null) {
    assigneeMember = teamMembers.find((m) => m.staffId === Number(apiCase.assigned_staff_id))
  }
  if (!assigneeMember && apiCase.assigned_user_id != null) {
    assigneeMember = teamMembers.find((m) => m.userId === Number(apiCase.assigned_user_id))
  }
  return {
    stage,
    stageLabel: resolveStageLabel(stage),
    _apiStatus: String(apiCase.status || 'INVESTIGATING').toUpperCase(),
    _dbId: apiCase.id,
    id: apiCase.case_number || apiCase.id,
    assignee: {
      userId: apiCase.assigned_user_id != null ? Number(apiCase.assigned_user_id) : null,
      name: assigneeMember?.name || apiCase.assigned_to || 'Unassigned',
    },
  }
}
